import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { t } from '@lingui/core/macro';
import ColorPicker from './ColorPicker';
import { VideoSettings } from './VideoSettings';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import {
  CardFramingArea,
  CardFramingBackgroundType,
  CardFramingSettings,
  getDefaultCardFramingAreaForBackgroundName,
  normalizeCardFramingArea,
  normalizeCardFramingSettings,
} from '../utils/cardFraming';
import './CardFramingPanel.css';
import { trackEvent } from '../utils/analytics';

interface CardFramingPanelProps {
  settings: VideoSettings;
  onSettingsChange: (settings: VideoSettings) => void | Promise<void>;
}

interface Point {
  x: number;
  y: number;
}

type ResizeChoice = 'stretch' | 'pad';

interface RatioMismatchPromptState {
  file: File;
  expectedRatio: number;
  expectedAspectLabel: string;
  actualRatio: number;
  actualWidth: number;
  actualHeight: number;
}

interface BackgroundAsset {
  name: string;
  url: string;
  width?: number;
  height?: number;
}

const IMAGE_RATIO_TOLERANCE = 0.01;

function parseAspectRatio(aspectRatio: string): number {
  const [widthRaw, heightRaw] = aspectRatio.split(':');
  const width = Number(widthRaw);
  const height = Number(heightRaw);
  if (!width || !height) return 16 / 9;
  return width / height;
}

function toCssAspectRatio(aspectRatio: string): string {
  const [widthRaw, heightRaw] = aspectRatio.split(':');
  const width = Number(widthRaw);
  const height = Number(heightRaw);
  if (!width || !height) return '16 / 9';
  return `${width} / ${height}`;
}

function toPercent(value: number): string {
  return Number((value * 100).toFixed(2)).toString();
}

function fromPercent(value: string): number {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.min(1, Math.max(0, numericValue / 100));
}

function buildAreaFromPoints(start: Point, end: Point): CardFramingArea {
  return normalizeCardFramingArea({
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  });
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = (): void => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = (): void => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image.'));
    };

    image.src = objectUrl;
  });
}

function greatestCommonDivisor(a: number, b: number): number {
  let x = Math.abs(Math.trunc(a));
  let y = Math.abs(Math.trunc(b));
  while (y !== 0) {
    const remainder = x % y;
    x = y;
    y = remainder;
  }
  return x || 1;
}

function formatImageRatio(width: number, height: number): string {
  if (!width || !height) {
    return '0:0';
  }
  const divisor = greatestCommonDivisor(width, height);
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
}

function formatRatioDecimal(ratio: number): string {
  return ratio.toFixed(4).replace(/0+$/u, '').replace(/\.$/u, '');
}

function normalizeAssetUrl(url: string): string {
  const parsed: URL = new URL(url.replace(/\\/g, '/'), window.location.origin);
  return parsed.href;
}

function safeDecodeUriComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function numberOrUndefined(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
}

const CardFramingPanel: React.FC<CardFramingPanelProps> = ({
  settings,
  onSettingsChange,
}) => {
  const { selectedProject } = useProject();
  const framing: CardFramingSettings = useMemo(
    () => normalizeCardFramingSettings(settings.card_framing),
    [settings.card_framing]
  );
  const [draftArea, setDraftArea] = useState<CardFramingArea>(framing.content_area);
  const [drawStart, setDrawStart] = useState<Point | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [ratioPrompt, setRatioPrompt] = useState<RatioMismatchPromptState | null>(null);
  const [isApplyingResize, setIsApplyingResize] = useState<boolean>(false);
  const [isUploadingBackground, setIsUploadingBackground] = useState<boolean>(false);
  const [isLoadingBackgrounds, setIsLoadingBackgrounds] = useState<boolean>(false);
  const [availableBackgrounds, setAvailableBackgrounds] = useState<BackgroundAsset[]>([]);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageAspectMismatch: boolean = useMemo(() => {
    if (!framing.background_image_width || !framing.background_image_height) return false;
    const targetRatio = parseAspectRatio(settings.aspect_ratio);
    const actualRatio = framing.background_image_width / framing.background_image_height;
    return Math.abs(actualRatio - targetRatio) / targetRatio > IMAGE_RATIO_TOLERANCE;
  }, [framing.background_image_height, framing.background_image_width, settings.aspect_ratio]);

  useEffect(() => {
    if (!drawStart) {
      setDraftArea(framing.content_area);
    }
  }, [drawStart, framing.content_area]);

  const commitFramingChange = (updates: Partial<CardFramingSettings>): void => {
    Object.keys(updates).forEach((field: string) => {
      trackEvent('project_setting_changed', {
        project_id: selectedProject?.id,
        settings_section: 'card_framing',
        property_name: field,
      }, {
        dedupeKey: `${selectedProject?.id ?? 'no_project'}:card_framing:${field}`,
      });
    });
    const nextFraming = normalizeCardFramingSettings({
      ...framing,
      ...updates,
      content_area: updates.content_area ?? framing.content_area,
    });
    const result = onSettingsChange({
      ...settings,
      card_framing: nextFraming,
    });
    if (result instanceof Promise) {
      result.catch((error) => {
        console.error('Error in onSettingsChange:', error);
      });
    }
  };

  const commitAreaChange = (area: CardFramingArea): void => {
    const nextArea = normalizeCardFramingArea(area);
    setDraftArea(nextArea);
    commitFramingChange({ content_area: nextArea });
  };

  const getPreviewPoint = (event: React.PointerEvent<HTMLDivElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0) return;
    const point = getPreviewPoint(event);
    setDrawStart(point);
    setDraftArea({ x: point.x, y: point.y, width: 0, height: 0 });
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!drawStart) return;
    setDraftArea(buildAreaFromPoints(drawStart, getPreviewPoint(event)));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!drawStart) return;
    const nextArea = buildAreaFromPoints(drawStart, getPreviewPoint(event));
    setDrawStart(null);
    commitAreaChange(nextArea);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleBackgroundTypeChange = (event: SelectChangeEvent<string>): void => {
    const backgroundType = event.target.value as CardFramingBackgroundType;
    commitFramingChange({ background_type: backgroundType });
  };

  const handleColorChange = (color: string): void => {
    commitFramingChange({
      background_type: 'color',
      background_color: color,
    });
  };

  const handleClearImage = (): void => {
    setUploadError(null);
    setRatioPrompt(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    commitFramingChange({
      background_type: framing.background_type === 'image' ? 'transparent' : framing.background_type,
      background_image_url: undefined,
      background_image_name: undefined,
      background_image_width: undefined,
      background_image_height: undefined,
    });
  };

  const upsertAvailableBackground = useCallback((asset: BackgroundAsset): void => {
    setAvailableBackgrounds((previous: BackgroundAsset[]) => {
      const existingIndex: number = previous.findIndex((candidate: BackgroundAsset) => candidate.url === asset.url);
      if (existingIndex === -1) {
        return [asset, ...previous];
      }
      const next: BackgroundAsset[] = [...previous];
      next[existingIndex] = asset;
      return next;
    });
  }, []);

  const applyResolvedImage = (
    imageUrl: string,
    imageName?: string,
    imageWidth?: number,
    imageHeight?: number
  ): void => {
    const backgroundNameForDefaults: string | undefined =
      imageName ?? safeDecodeUriComponent(imageUrl.split('/').pop() ?? '');
    const defaultArea: CardFramingArea | undefined =
      getDefaultCardFramingAreaForBackgroundName(backgroundNameForDefaults);

    commitFramingChange({
      background_type: 'image',
      background_image_url: imageUrl,
      background_image_name: imageName,
      background_image_width: imageWidth,
      background_image_height: imageHeight,
      ...(defaultArea ? { content_area: defaultArea } : {}),
    });
  };

  const loadAvailableBackgrounds = useCallback(async (): Promise<void> => {
    if (!selectedProject?.id) {
      setAvailableBackgrounds([]);
      return;
    }
    setIsLoadingBackgrounds(true);
    try {
      const response = await authFetch(`${getApiUrl()}/available-card-backgrounds?projectId=${selectedProject.id}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const result = await response.json();
      const payload: unknown[] = Array.isArray(result.backgrounds)
        ? result.backgrounds
        : Array.isArray(result.files)
          ? result.files
          : [];
      if (result.status !== 'OK' || payload.length === 0) {
        setAvailableBackgrounds([]);
        return;
      }
      const mapped: BackgroundAsset[] = payload
        .map((background: any): BackgroundAsset | null => {
          if (!background || typeof background.url !== 'string') return null;
          return {
            name: typeof background.name === 'string' && background.name
              ? background.name
              : background.url.split('/').pop() ?? background.url,
            url: normalizeAssetUrl(background.url),
            width: numberOrUndefined(background.width),
            height: numberOrUndefined(background.height),
          };
        })
        .filter((background: BackgroundAsset | null): background is BackgroundAsset => background !== null);
      setAvailableBackgrounds(mapped);
    } catch (error) {
      console.error('Failed to load card backgrounds', error);
      setAvailableBackgrounds([]);
    } finally {
      setIsLoadingBackgrounds(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    void loadAvailableBackgrounds();
  }, [loadAvailableBackgrounds]);

  const uploadBackgroundFile = useCallback(async (
    file: File,
    dimensions?: { width?: number; height?: number }
  ): Promise<BackgroundAsset | null> => {
    if (!selectedProject?.id) {
      setUploadError(String(t`No project selected.`));
      return null;
    }

    setIsUploadingBackground(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch(
        `${getApiUrl()}/upload-card-background?projectId=${selectedProject.id}`,
        { method: 'POST', body: formData }
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.status !== 'OK') {
        throw new Error(result.message || 'Upload failed');
      }

      const fallbackFileName: string = encodeURIComponent(file.name);
      const fallbackUrl: string = `/users/${selectedProject.id}/backgrounds/${fallbackFileName}`;
      const asset: BackgroundAsset = {
        name: typeof result.name === 'string' && result.name
          ? result.name
          : file.name.replace(/\.[^/.]+$/u, ''),
        url: typeof result.url === 'string' && result.url
          ? normalizeAssetUrl(result.url)
          : fallbackUrl,
        width: dimensions?.width ?? numberOrUndefined(result.width),
        height: dimensions?.height ?? numberOrUndefined(result.height),
      };

      upsertAvailableBackground(asset);
      return asset;
    } catch (error) {
      console.error('Failed to upload card background', error);
      setUploadError(String(t`Failed to upload the selected image.`));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return null;
    } finally {
      setIsUploadingBackground(false);
    }
  }, [selectedProject?.id, upsertAvailableBackground]);

  const getResizeTargetDimensions = (
    width: number,
    height: number,
    expectedRatio: number
  ): { width: number; height: number } => {
    const actualRatio = width / height;
    if (actualRatio > expectedRatio) {
      return {
        width,
        height: Math.max(1, Math.round(width / expectedRatio)),
      };
    }
    return {
      width: Math.max(1, Math.round(height * expectedRatio)),
      height,
    };
  };

  const applyResizeChoice = async (choice: ResizeChoice): Promise<void> => {
    if (!ratioPrompt) return;
    setIsApplyingResize(true);
    setUploadError(null);
    try {
      const source = await loadImageFromFile(ratioPrompt.file);
      const target = getResizeTargetDimensions(
        ratioPrompt.actualWidth,
        ratioPrompt.actualHeight,
        ratioPrompt.expectedRatio
      );
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = target.width;
      outputCanvas.height = target.height;
      const context = outputCanvas.getContext('2d');
      if (!context) {
        throw new Error('Canvas context unavailable.');
      }
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      if (choice === 'stretch') {
        context.drawImage(source, 0, 0, target.width, target.height);
      } else {
        const scale = Math.min(target.width / source.naturalWidth, target.height / source.naturalHeight);
        const fittedWidth = Math.max(1, Math.round(source.naturalWidth * scale));
        const fittedHeight = Math.max(1, Math.round(source.naturalHeight * scale));
        const dx = Math.floor((target.width - fittedWidth) / 2);
        const dy = Math.floor((target.height - fittedHeight) / 2);
        context.clearRect(0, 0, target.width, target.height);
        context.drawImage(source, dx, dy, fittedWidth, fittedHeight);
      }

      const blob: Blob = await new Promise<Blob>((resolve, reject) => {
        outputCanvas.toBlob((canvasBlob: Blob | null) => {
          if (!canvasBlob) {
            reject(new Error('Failed to generate image blob.'));
            return;
          }
          resolve(canvasBlob);
        }, 'image/png');
      });

      const baseName: string = ratioPrompt.file.name.replace(/\.[^/.]+$/u, '');
      const suffix: string = choice === 'stretch' ? 'stretched' : 'padded';
      const outputFile: File = new File(
        [blob],
        `${baseName}_${suffix}_${target.width}x${target.height}.png`,
        { type: 'image/png' }
      );
      const uploaded: BackgroundAsset | null = await uploadBackgroundFile(outputFile, {
        width: target.width,
        height: target.height,
      });
      if (uploaded) {
        applyResolvedImage(uploaded.url, uploaded.name, uploaded.width, uploaded.height);
      }
    } catch {
      setUploadError(String(t`Failed to process the selected image.`));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsApplyingResize(false);
      setRatioPrompt(null);
    }
  };

  const handleRatioPromptCancel = (): void => {
    setRatioPrompt(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateAndReadImage = async (file: File): Promise<void> => {
    setUploadError(null);
    setRatioPrompt(null);
    try {
      const image = await loadImageFromFile(file);
      const targetRatio = parseAspectRatio(settings.aspect_ratio);
      const actualRatio = image.naturalWidth / image.naturalHeight;

      if (!image.naturalWidth || !image.naturalHeight || Math.abs(actualRatio - targetRatio) / targetRatio > IMAGE_RATIO_TOLERANCE) {
        setRatioPrompt({
          file,
          expectedRatio: targetRatio,
          expectedAspectLabel: settings.aspect_ratio,
          actualRatio,
          actualWidth: image.naturalWidth,
          actualHeight: image.naturalHeight,
        });
        return;
      }

      const uploaded: BackgroundAsset | null = await uploadBackgroundFile(file, {
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
      if (uploaded) {
        applyResolvedImage(uploaded.url, uploaded.name, uploaded.width, uploaded.height);
      }
    } catch {
      setUploadError(String(t`Failed to load the selected image.`));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const currentBackgroundOptions: BackgroundAsset[] = useMemo(() => {
    const options: BackgroundAsset[] = [...availableBackgrounds];
    if (framing.background_image_url) {
      const exists: boolean = options.some((background: BackgroundAsset) => background.url === framing.background_image_url);
      if (!exists) {
        options.unshift({
          name: framing.background_image_name || (framing.background_image_url.split('/').pop() ?? String(t`Current background`)),
          url: framing.background_image_url,
          width: framing.background_image_width,
          height: framing.background_image_height,
        });
      }
    }
    return options;
  }, [
    availableBackgrounds,
    framing.background_image_height,
    framing.background_image_name,
    framing.background_image_url,
    framing.background_image_width,
  ]);

  const handleSavedBackgroundChange = (event: SelectChangeEvent<string>): void => {
    const selectedUrl: string = event.target.value;
    if (!selectedUrl) {
      handleClearImage();
      return;
    }
    const selectedAsset: BackgroundAsset | undefined = currentBackgroundOptions.find(
      (background: BackgroundAsset) => background.url === selectedUrl
    );
    if (!selectedAsset) return;
    setUploadError(null);
    setRatioPrompt(null);
    trackEvent('project_setting_changed', {
      project_id: selectedProject?.id,
      settings_section: 'card_framing',
      property_name: 'saved_background_selected',
      background_name: selectedAsset.name,
    });
    applyResolvedImage(
      selectedAsset.url,
      selectedAsset.name,
      selectedAsset.width,
      selectedAsset.height
    );
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    trackEvent('card_background_upload_started', {
      project_id: selectedProject?.id,
      file_name: file.name,
    });
    void validateAndReadImage(file);
  };

  const handleAreaInputChange = (field: keyof CardFramingArea, value: string): void => {
    const nextValue = fromPercent(value);
    const current = draftArea;
    let nextArea: CardFramingArea = current;

    if (field === 'x') {
      nextArea = { ...current, x: Math.min(nextValue, 1 - current.width) };
    } else if (field === 'y') {
      nextArea = { ...current, y: Math.min(nextValue, 1 - current.height) };
    } else if (field === 'width') {
      nextArea = { ...current, width: Math.min(nextValue, 1 - current.x) };
    } else {
      nextArea = { ...current, height: Math.min(nextValue, 1 - current.y) };
    }

    commitAreaChange(nextArea);
  };

  const handleResetArea = (): void => {
    commitAreaChange({ x: 0, y: 0, width: 1, height: 1 });
  };

  const previewStyle: React.CSSProperties = {
    aspectRatio: toCssAspectRatio(settings.aspect_ratio),
  };

  if (framing.background_type === 'image' && framing.background_image_url) {
    previewStyle.backgroundImage = `url("${normalizeAssetUrl(framing.background_image_url)}")`;
  } else if (framing.background_type === 'color') {
    previewStyle.backgroundColor = framing.background_color;
  }

  return (
    <div className="card-framing-panel">
      <div className="card-framing-controls">
        <div className="setting-item">
          <label>{t`Background`}</label>
          <div className="input-wrapper card-framing-input-wrapper">
            <Select
              value={framing.background_type}
              onChange={handleBackgroundTypeChange}
              className="video-setting-select card-framing-select"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="image">{t`Image`}</MenuItem>
              <MenuItem value="color">{t`Solid Color`}</MenuItem>
              <MenuItem value="transparent">{t`Transparent`}</MenuItem>
            </Select>
          </div>
        </div>

        {framing.background_type === 'color' && (
          <div className="setting-item">
            <label>{t`Frame Color`}</label>
            <div className="input-wrapper card-framing-input-wrapper">
              <ColorPicker value={framing.background_color} onChange={handleColorChange} />
            </div>
          </div>
        )}

        <div className="setting-item card-framing-upload-row">
          <label>{t`Card Background`}</label>
          <div className="input-wrapper card-framing-upload-controls">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              disabled={isUploadingBackground || isApplyingResize}
              onChange={handleImageUpload}
            />
            {framing.background_image_url && (
              <button
                type="button"
                className="card-framing-secondary-button"
                onClick={handleClearImage}
                disabled={isUploadingBackground || isApplyingResize}
              >
                {t`Clear`}
              </button>
            )}
          </div>
        </div>

        <div className="setting-item">
          <label>{t`Saved Backgrounds`}</label>
          <div className="input-wrapper card-framing-input-wrapper">
            <Select
              value={framing.background_image_url || ''}
              onChange={handleSavedBackgroundChange}
              className="video-setting-select card-framing-select"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
              disabled={isUploadingBackground || isApplyingResize}
              displayEmpty
            >
              <MenuItem value="">{isLoadingBackgrounds ? t`Loading...` : t`None`}</MenuItem>
              {currentBackgroundOptions.map((background: BackgroundAsset) => (
                <MenuItem key={background.url} value={background.url}>
                  {background.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {(uploadError || imageAspectMismatch) && (
          <div className="setting-item card-framing-message-row">
            <span />
            <p className="card-framing-error">
              {uploadError || t`The uploaded image no longer matches the selected aspect ratio.`}
            </p>
          </div>
        )}

        <div className="setting-item card-framing-message-row">
          <span />
          <p className="card-framing-hint">
            {t`Draw the rectangle on the frame, or enter percentages below.`}
          </p>
        </div>
      </div>

      <div
        ref={previewRef}
        className={`card-framing-preview${framing.background_type === 'transparent' || (framing.background_type === 'image' && !framing.background_image_url) ? ' card-framing-preview-checker' : ''}`}
        style={previewStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => setDrawStart(null)}
        role="presentation"
      >
        <div
          className="card-framing-rect"
          style={{
            left: `${draftArea.x * 100}%`,
            top: `${draftArea.y * 100}%`,
            width: `${draftArea.width * 100}%`,
            height: `${draftArea.height * 100}%`,
          }}
        />
      </div>

      <div className="card-framing-area-row">
        {(['x', 'y', 'width', 'height'] as Array<keyof CardFramingArea>).map((field) => (
          <label key={field} className="card-framing-area-field">
            <span className="card-framing-area-label">
              {field === 'x' ? t`X`
                : field === 'y' ? t`Y`
                  : field === 'width' ? t`Width`
                    : t`Height`}
            </span>
            <span className="card-framing-value-control">
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={toPercent(draftArea[field])}
                onChange={(event) => handleAreaInputChange(field, event.target.value)}
              />
              <span className="card-framing-unit">%</span>
            </span>
          </label>
        ))}
        <button
          type="button"
          className="card-framing-secondary-button card-framing-reset-button"
          onClick={handleResetArea}
          disabled={isUploadingBackground || isApplyingResize}
        >
          {t`Reset Frame`}
        </button>
      </div>

      <Dialog
        open={ratioPrompt !== null}
        onClose={isApplyingResize || isUploadingBackground ? undefined : handleRatioPromptCancel}
      >
        <DialogTitle>{t`Aspect Ratio Mismatch`}</DialogTitle>
        <DialogContent>
          {ratioPrompt && (
            <>
              <p className="card-framing-dialog-line">
                {t`Expected ratio: ${ratioPrompt.expectedAspectLabel} (${formatRatioDecimal(ratioPrompt.expectedRatio)})`}
              </p>
              <p className="card-framing-dialog-line">
                {t`Actual ratio: ${formatImageRatio(ratioPrompt.actualWidth, ratioPrompt.actualHeight)} (${formatRatioDecimal(ratioPrompt.actualRatio)})`}
              </p>
              <p className="card-framing-dialog-hint">
                {t`Choose how to adapt the uploaded image to the expected ratio.`}
              </p>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRatioPromptCancel} disabled={isApplyingResize || isUploadingBackground}>
            {t`Cancel`}
          </Button>
          <Button onClick={() => { void applyResizeChoice('stretch'); }} disabled={isApplyingResize || isUploadingBackground}>
            {t`Stretch`}
          </Button>
          <Button variant="contained" onClick={() => { void applyResizeChoice('pad'); }} disabled={isApplyingResize || isUploadingBackground}>
            {t`Pad`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CardFramingPanel;
